import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

/**
 * Card variants provide visual hierarchy (reskin 2026-07-31: static
 * surfaces carry NO shadow — the hairline border + whitespace do the
 * separating; shadows are reserved for floating layers):
 * - default: hairline border, no shadow
 * - elevated: soft shadow — opt-in, floating/spotlight content only
 * - interactive: clickable; hover lifts 1px with a quiet shadow
 * - accent: gold-tinted background for featured/highlighted content
 * - flat: alias of the shadowless look (kept for callers)
 */
const cardVariants = cva(
    "rounded-lg border bg-[var(--bg-surface)] text-[var(--text-primary)] transition-all duration-200",
    {
        variants: {
            variant: {
                default:
                    "border-[var(--border-subtle)]",
                elevated:
                    "border-[var(--border-subtle)] shadow-[var(--shadow-md)]",
                interactive:
                    "border-[var(--border-subtle)] hover:shadow-[var(--shadow-card)] hover:-translate-y-px hover:border-[var(--primary-200)] cursor-pointer",
                accent:
                    "border-[var(--border-subtle)] bg-[var(--bg-accent-subtle)]",
                flat:
                    "border-[var(--border-subtle)] shadow-none",
            },
        },
        defaultVariants: {
            variant: "default",
        },
    }
)

export interface CardProps
    extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> { }

const Card = React.forwardRef<HTMLDivElement, CardProps>(
    ({ className, variant, ...props }, ref) => (
        <div
            ref={ref}
            className={cn(cardVariants({ variant, className }))}
            {...props}
        />
    )
)
Card.displayName = "Card"

const CardHeader = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
    <div
        ref={ref}
        className={cn("flex flex-col space-y-1.5 p-6", className)}
        {...props}
    />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
    HTMLParagraphElement,
    React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
    <h3
        ref={ref}
        className={cn(
            "font-serif text-xl font-medium leading-7",
            className
        )}
        {...props}
    />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
    HTMLParagraphElement,
    React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
    <p
        ref={ref}
        className={cn("text-sm text-[var(--text-secondary)]", className)}
        {...props}
    />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
    <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
    <div
        ref={ref}
        className={cn("flex items-center p-6 pt-0", className)}
        {...props}
    />
))
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
